import { create } from 'zustand';
import type { Experiment } from '@/types/experiment';
import { getAllExperiments, updateExperiment, createExperiment } from '@/services/api';

interface ExperimentStore {
  // Experiment Data (Database-Aligned)
  allExperiments: Record<string, Experiment>;
  isLoading: boolean;
  error: string | null;

  // Actions
  fetchAllExperiments: () => Promise<void>;
  updateExperimentFromWebSocket: (exp_id: string, data: Partial<Experiment>) => void;
  updateExperimentFromUser: (exp_id: string, data: Partial<Experiment>) => Promise<boolean>;
  createExperiment: (experiment: Omit<Experiment, 'exp_id'>) => Promise<Experiment | null>;
  generateNextExpId: () => string;
}

export const useExperimentStore = create<ExperimentStore>((set, get) => ({
  // Initialize with default values
  allExperiments: {},
  isLoading: false,
  error: null,

  fetchAllExperiments: async () => {
    set({ isLoading: true, error: null });
    try {
      const experimentsData = await getAllExperiments();
      // Convert string dates to Date objects for all experiments
      const convertedExperiments: Record<string, Experiment> = {};
      experimentsData.forEach((experiment) => {
        convertedExperiments[experiment.exp_id] = {
          ...experiment,
          start_time: experiment.start_time ? new Date(experiment.start_time) : null,
          end_time: experiment.end_time ? new Date(experiment.end_time) : null,
          exp_status_time: experiment.exp_status_time ? new Date(experiment.exp_status_time) : null,
        };
      });
      // ✅ Fix 2: Merge instead of replace - preserve WebSocket updates
      set((state) => ({
        allExperiments: {
          ...state.allExperiments,  // Keep existing store data (may have WebSocket updates)
          ...convertedExperiments,  // Merge with API data (adds new entities, updates existing)
        },
        isLoading: false,
      }));
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'Failed to fetch experiments',
        isLoading: false,
        allExperiments: {}
      });
    }
  },

  updateExperimentFromWebSocket: (exp_id: string, data: Partial<Experiment>) => {
    set((state) => {
      const updatedExperiments = {
        ...state.allExperiments,
        [exp_id]: {
          ...state.allExperiments[exp_id],
          ...data,
        },
      };
      
      return { allExperiments: updatedExperiments };
    });
  },

  updateExperimentFromUser: async (exp_id: string, data: Partial<Experiment>) => {
    
    // VALIDATION: Ensure experiment exists in store before any operation
    const originalData = get().allExperiments[exp_id];
    if (!originalData) {
      console.error('🔄 [EXPERIMENT STORE] ERROR: Cannot update non-existent experiment:', exp_id);
      // For new experiments, we need to create them in the database first
      // This method should only be called for existing experiments
      return false;
    }
    
    // Remove redundant type validation - this should be handled at UI level
    // Store should focus on data persistence, not validation

    try {
      // Update store immediately (store-first approach)
      set((state) => {
        const updatedExperiments = {
          ...state.allExperiments,
          [exp_id]: {
            ...state.allExperiments[exp_id],
            ...data,
          },
        };
        
        return { allExperiments: updatedExperiments };
      });

      // Sync to database using new specific API method
      const success = await updateExperiment(exp_id, data);
      
      if (!success) {
        // Revert store update on failure using captured original data
        set((state) => ({
          allExperiments: {
            ...state.allExperiments,
            [exp_id]: originalData,
          },
        }));
        return false;
      }

      return true;
    } catch (error) {
      console.error('🔄 [EXPERIMENT STORE] Error updating experiment:', error);
      // Also revert on exception using captured original data
      if (originalData) {
        set((state) => ({
          allExperiments: {
            ...state.allExperiments,
            [exp_id]: originalData,
          },
        }));
      }
      return false;
    }
  },

  generateNextExpId: () => {
    const existingIds = Object.keys(get().allExperiments);
    
    // If no experiments exist, start with E001
    if (existingIds.length === 0) {
      return 'E001';
    }
    
    // Find the highest experiment number
    let maxNum = 0;
    existingIds.forEach(id => {
      const match = id.match(/^E(\d+)$/);
      if (match) {
        const num = parseInt(match[1]);
        if (num > maxNum) maxNum = num;
      }
    });
    
    // Generate next ID
    const nextNum = maxNum + 1;
    return `E${nextNum.toString().padStart(3, '0')}`;
  },

  createExperiment: async (experiment: Omit<Experiment, 'exp_id'>) => {
    try {
      // Create in database (backend generates exp_id)
      const createdExperiment = await createExperiment(experiment);
      
      if (createdExperiment) {
        // Add to store after successful database creation
        set((state) => ({
          allExperiments: {
            ...state.allExperiments,
            [createdExperiment.exp_id]: createdExperiment,
          },
        }));
        return createdExperiment;
      }
      return null;
    } catch (error) {
      console.error('🔄 [EXPERIMENT STORE] Error creating experiment:', error);
      return null;
    }
  },
}));

export default useExperimentStore; 